import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Carousel } from 'react-bootstrap';
import ImageAttribution from '../media/ImageAttribution';
import { retryThenPlaceholder } from '../../utils/imageResolver';
import { NO_PHOTO_SRC } from '../../constants/noPhoto';

/**
 * PhotoCarousel — customer-facing image carousel for the product detail
 * page (Photo Cascade P2-01, S2-01.1).
 *
 * - Auto-advances (~5s), pauses on hover, wraps, shows indicators + arrows.
 * - Manual controls + native touch/swipe (react-bootstrap Carousel handles
 *   swipe via the `touch` prop).
 * - Respects prefers-reduced-motion: no auto-advance for those users.
 * - A single image renders as a plain <img> (no carousel chrome).
 * - Per-slide credit line (license attribution for iNat/Smithsonian images
 *   is legally required to display).
 *
 * @param {Array<{url, thumbUrl, credit}>} images
 * @param {string} alt
 */
function PhotoCarousel({ images = [], alt = '', manual = false, hoverAdvance = false, maxSlides = 10, imgClassName = 'frog-detail-image', eagerFirst = true }) {
  const [reducedMotion, setReducedMotion] = useState(false);
  // hover-to-advance (operator pick 2026-07-30): idle cards stay still
  // (comparison-safe); the hovered/touched card cycles its photos.
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const handler = (e) => setReducedMotion(e.matches);
    // addEventListener is the modern API; older Safari uses addListener
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);

  const valid = (images || []).filter((img) => img && img.url).slice(0, maxSlides);
  if (valid.length === 0) {
    return (
      <img
        className={imgClassName}
        src={NO_PHOTO_SRC}
        data-no-photo="1"
        alt={alt}
      />
    );
  }

  const renderCredit = (credit) =>
    credit ? <div className="photo-carousel__credit">{credit}</div> : null;

  /* #110e — per-slide credit content. The attribution TEXT wins when
     present (full legal string); a CC slide whose text is empty but
     whose asset carries source/license (creditInfo) renders the same
     small ImageAttribution pill the card covers use, instead of
     rendering NOTHING — CC-BY attribution is a license obligation. */
  const slideCredit = (img) => {
    if (img.credit) {
      return <span className="photo-carousel__credit">{img.credit}</span>;
    }
    if (img.creditInfo) {
      return <ImageAttribution asset={img.creditInfo} variant="pill" />;
    }
    return null;
  };
  const slideHasCaption = (img) => !!(img.credit || img.creditInfo || img.label);

  // Single image → no carousel chrome. Credit OVERLAYS the photo
  // (same scrim style as multi-slide captions) — an in-flow credit
  // line made single-image cards taller than creditless ones
  // (operator 2026-08-02: "cards all same size even if there's one
  // of those little comments on it").
  if (valid.length === 1) {
    const only = valid[0];
    return (
      <div className="photo-carousel photo-carousel--single">
        <img
          className={imgClassName}
          src={only.url}
          alt={alt}
          // #99: was hardcoded lazy — inconsistent with the multi-slide
          // path, which eager-loads its first (visible) image. The card's
          // one visible photo being lazy is the only path where a card can
          // sit with its image unrequested; callers below the fold can
          // opt back into lazy via eagerFirst={false}.
          loading={eagerFirst ? 'eager' : 'lazy'}
          onError={retryThenPlaceholder}
        />
        {slideHasCaption(only) && (
          <div className="photo-carousel__caption photo-carousel__caption--static">
            {only.label && (
              <span className="photo-carousel__label">{only.label}</span>
            )}
            {slideCredit(only)}
          </div>
        )}
      </div>
    );
  }

  return (
    <Carousel
      className="photo-carousel"
      interval={
        reducedMotion ? null
          : hoverAdvance ? (hovered ? 3500 : null)
          : manual ? null
          : 5000
      }
      pause={hoverAdvance ? false : 'hover'}
      onMouseEnter={hoverAdvance ? () => setHovered(true) : undefined}
      onMouseLeave={hoverAdvance ? () => setHovered(false) : undefined}
      onTouchStart={hoverAdvance ? () => setHovered(true) : undefined}
      // #99: touch-start with no touch-end left `hovered` stuck true —
      // one tap on mobile auto-advanced the carousel forever.
      onTouchEnd={hoverAdvance ? () => setHovered(false) : undefined}
      onTouchCancel={hoverAdvance ? () => setHovered(false) : undefined}
      touch
      indicators
      controls
      wrap
      aria-label={alt ? `${alt} photo gallery` : 'Product photo gallery'}
    >
      {valid.map((img, i) => (
        <Carousel.Item key={img.url || i}>
          <img
            className={`${imgClassName} d-block w-100`}
            src={img.url}
            alt={alt ? `${alt} — photo ${i + 1} of ${valid.length}` : `Photo ${i + 1}`}
            // #99 root cause: slides 1+ were ALWAYS lazy, but Bootstrap
            // sets inactive .carousel-item to display:none — a lazy image
            // with no box is never fetched at any scroll position, so
            // hover-advance rotated onto blank frames (and a late fetch
            // failure then blanked them permanently). Only the in-flow
            // FIRST slide may be lazy (for below-fold cards); hidden
            // slides must be eager or they never load at all.
            loading={i === 0 && !eagerFirst ? 'lazy' : 'eager'}
            onError={retryThenPlaceholder}
          />
          {slideHasCaption(img) ? (
            <Carousel.Caption className="photo-carousel__caption">
              {/* I-02d: optional per-slide label (e.g. lot-member name) —
                  distinct from `credit` (legally-required license
                  attribution); both share the scrim. */}
              {img.label ? (
                <span className="photo-carousel__label">{img.label}</span>
              ) : null}
              {slideCredit(img)}
            </Carousel.Caption>
          ) : null}
        </Carousel.Item>
      ))}
    </Carousel>
  );
}

PhotoCarousel.propTypes = {
  manual: PropTypes.bool,
  hoverAdvance: PropTypes.bool,
  maxSlides: PropTypes.number,
  imgClassName: PropTypes.string,
  eagerFirst: PropTypes.bool,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string,
      thumbUrl: PropTypes.string,
      credit: PropTypes.string,
      // #110e — structured attribution (same shape ImageAttribution
      // takes); renders a license pill when `credit` text is absent.
      creditInfo: PropTypes.shape({
        source: PropTypes.string,
        source_license: PropTypes.string,
        source_attribution: PropTypes.string,
      }),
      label: PropTypes.string,
    })
  ),
  alt: PropTypes.string,
};

export default PhotoCarousel;
